import React from 'react';
import { getConfig } from '../config';

interface StreamRow {
  id: string;
  workspaceName: string;
  workspaceSlug?: string;
  cameraName: string;
  cameraSlug?: string;
  isEnabled: boolean;
  isActive: boolean;
  status: 'Disabled' | 'OK' | 'ERROR';
  hlsUrl?: string;
  displayOrder?: number;
}

const SystemHealth: React.FC = () => {
  const { apiBase } = getConfig();
  const [streams, setStreams] = React.useState<StreamRow[]>([]);
  const [workspaces, setWorkspaces] = React.useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [workspaceId, setWorkspaceId] = React.useState<string>('');
  const hasSetDefaultWorkspaceRef = React.useRef(false);
  const [pending, setPending] = React.useState<Record<string, boolean>>({});
  const [batchPending, setBatchPending] = React.useState<'start' | 'stop' | null>(null);
  const [bulkMode, setBulkMode] = React.useState<'start' | 'stop' | null>(null);
  const [inFlightIds, setInFlightIds] = React.useState<Record<string, boolean>>({});
  const [doneIds, setDoneIds] = React.useState<Record<string, 'ok' | 'error'>>({});
  const [optionsLoaded, setOptionsLoaded] = React.useState(false);
  const currentRequestIdRef = React.useRef(0);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/superadmin/workspaces/options`, { credentials: 'include' });
        if (res.ok) {
          const data = (await res.json()) as { id: number; name: string }[];
          setWorkspaces(data);
          // Default to ATA on initial load if present (run only once)
          if (!hasSetDefaultWorkspaceRef.current) {
            const defaultWs = data.find(w => String(w.name).toLowerCase() === 'ata');
            if (defaultWs) setWorkspaceId(String(defaultWs.id));
            hasSetDefaultWorkspaceRef.current = true;
          }
          setOptionsLoaded(true);
        }
      } catch {}
    })();
  }, [apiBase]);

  const fetchStreams = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const requestId = ++currentRequestIdRef.current;
      const url = new URL(`${apiBase}/superadmin/streams`);
      if (workspaceId) url.searchParams.set('workspaceId', workspaceId);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
      const data = await res.json();
      if (requestId === currentRequestIdRef.current) {
        setStreams(Array.isArray(data) ? data : []);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load streams');
    } finally {
      setLoading(false);
    }
  }, [apiBase, workspaceId]);

  React.useEffect(() => {
    if (!optionsLoaded) return;
    fetchStreams();
  }, [fetchStreams, optionsLoaded]);

  const act = async (id: string, action: 'start' | 'stop') => {
    try {
      setPending(p => ({ ...p, [id]: true }));
      const res = await fetch(`${apiBase}/superadmin/streams/${id}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `Failed to ${action}`);
      }
      // Small delay then refresh for accurate isActive
      setTimeout(fetchStreams, 1500);
    } catch (e: any) {
      alert(`Failed to ${action} stream: ${e.message || e}`);
    } finally {
      setPending(p => ({ ...p, [id]: false }));
    }
  };

  const bulkAct = async (action: 'start' | 'stop') => {
    try {
      setBatchPending(action);
      setBulkMode(action);
      setDoneIds({});
      setInFlightIds({});
      // Determine target cameras based on desired action
      const targets = streams
        .filter(s => s.isEnabled && (action === 'stop' ? s.isActive : !s.isActive))
        .map(s => s.id);
      const limit = 4;
      let idx = 0;

      const startNext = async (): Promise<void> => {
        if (idx >= targets.length) return;
        const id = targets[idx++];
        setInFlightIds(prev => ({ ...prev, [id]: true }));
        try {
          const res = await fetch(`${apiBase}/superadmin/streams/${id}/${action}`, {
            method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          });
          if (!res.ok) throw new Error(await res.text());
          setDoneIds(prev => ({ ...prev, [id]: 'ok' }));
        } catch (e) {
          setDoneIds(prev => ({ ...prev, [id]: 'error' }));
        } finally {
          setInFlightIds(prev => ({ ...prev, [id]: false }));
          await fetchStreams();
          if (idx < targets.length) await startNext();
        }
      };

      // Launch up to limit workers
      const starters = Math.min(limit, targets.length);
      const workers: Promise<void>[] = [];
      for (let i = 0; i < starters; i++) {
        workers.push(startNext());
      }
      await Promise.all(workers);
      // Final refresh
      await fetchStreams();
    } finally {
      setBatchPending(null);
      setBulkMode(null);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Live Streams</h1>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-sm">Workspace</label>
        <select
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">All</option>
          {workspaces.map(w => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
        <button className="px-3 py-1 bg-mc-amber text-black rounded" onClick={fetchStreams} disabled={loading}>Refresh</button>
        <div className="ml-auto flex items-center gap-2 w-full sm:w-auto justify-end">
          <button
            className="px-3 py-1 bg-green-600 text-white rounded disabled:opacity-50"
            disabled={batchPending !== null || loading}
            onClick={() => bulkAct('start')}
          >{batchPending === 'start' ? 'Starting All…' : 'Start All'}</button>
          <button
            className="px-3 py-1 bg-red-700 text-white rounded disabled:opacity-50"
            disabled={batchPending !== null || loading}
            onClick={() => bulkAct('stop')}
          >{batchPending === 'stop' ? 'Stopping All…' : 'Stop All'}</button>
          {batchPending && <span className="inline-block w-4 h-4 border-2 border-mc-amber border-t-transparent rounded-full animate-spin" />}
        </div>
      </div>

      {error && <div className="text-red-600 mb-2">{error}</div>}
      <h2 className="text-xl font-semibold mb-2">Camera Streams</h2>
      <div className="overflow-x-auto mb-6">
      <table className="min-w-full bg-white shadow rounded overflow-hidden">
        <thead className="bg-gray-100 text-gray-700">
          <tr>
            <th className="text-left p-3">Camera</th>
            <th className="text-left p-3">Workspace</th>
            <th className="text-left p-3">Status</th>
            <th className="text-left p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {streams.map(s => (
            <tr key={s.id} className="border-b last:border-none">
              <td className="p-3 whitespace-nowrap">{s.cameraName}</td>
              <td className="p-3">{s.workspaceName}</td>
              <td className="p-3">
                {s.status === 'OK' && <span className="text-green-600 font-semibold">Online</span>}
                {s.status === 'ERROR' && <span className="text-red-600 font-semibold">Offline</span>}
                {s.status === 'Disabled' && <span className="text-gray-500 font-semibold">Disabled</span>}
              </td>
              <td className="p-3">
                <div className="flex items-center gap-2">
                  <button className="px-2 py-1 bg-green-500 text-white rounded disabled:opacity-50" disabled={!s.isEnabled || s.isActive || loading || pending[s.id] || inFlightIds[s.id]} onClick={() => act(s.id, 'start')}>{(pending[s.id] || inFlightIds[s.id]) ? 'Starting…' : 'Start'}</button>
                  <button className="px-2 py-1 bg-red-600 text-white rounded disabled:opacity-50" disabled={!s.isEnabled || !s.isActive || loading || pending[s.id] || inFlightIds[s.id]} onClick={() => act(s.id, 'stop')}>{(pending[s.id] || inFlightIds[s.id]) ? 'Stopping…' : 'Stop'}</button>
                  {(pending[s.id] || inFlightIds[s.id]) && <span className="inline-block w-4 h-4 border-2 border-mc-amber border-t-transparent rounded-full animate-spin" aria-label="loading" />}
                  {doneIds[s.id] === 'ok' && <span className="text-green-600 text-xs">Action Completed</span>}
                  {doneIds[s.id] === 'error' && <span className="text-red-600 text-xs">Error</span>}
                  {bulkMode && doneIds[s.id] === 'ok' && <span className="text-green-600 text-xs">Action Completed</span>}
                  {bulkMode && doneIds[s.id] === 'error' && <span className="text-red-600 text-xs">Error</span>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default SystemHealth;


